import type { OperationOptions } from '@/features/veluvit/services/api'

/** 붙여넣은 텍스트의 명시적 라벨은 로컬에서 분류하고, 필드 확장은 제작 보조 Operation을 사용한다. */
export const IMPORT_MAX_CHARS = 50000

type ImportResult = {
  character: {
    name?: string
    tagline?: string
    persona?: string
    habits: string[]
    forbidden: string[]
  }
  intro?: { context?: string; greeting?: string }
  world?: { name?: string; body: string }
  lorebook?: string
}

type Field =
  | 'name'
  | 'tagline'
  | 'persona'
  | 'habits'
  | 'forbidden'
  | 'worldName'
  | 'world'
  | 'context'
  | 'greeting'
  | 'lorebook'
const LABELS: Record<string, Field> = {
  이름: 'name',
  name: 'name',
  캐릭터명: 'name',
  캐릭터이름: 'name',
  소개: 'tagline',
  한줄소개: 'tagline',
  tagline: 'tagline',
  성격: 'persona',
  설정: 'persona',
  캐릭터설정: 'persona',
  캐릭터프롬프트: 'persona',
  페르소나: 'persona',
  persona: 'persona',
  말투: 'habits',
  말버릇: 'habits',
  대사: 'habits',
  금지: 'forbidden',
  금지사항: 'forbidden',
  거절할요청: 'forbidden',
  forbidden: 'forbidden',
  세계관이름: 'worldName',
  세계관: 'world',
  세계관설정: 'world',
  배경: 'world',
  world: 'world',
  상황: 'context',
  시작상황: 'context',
  숨은시작상황: 'context',
  context: 'context',
  첫대사: 'greeting',
  첫메시지: 'greeting',
  greeting: 'greeting',
  로어북: 'lorebook',
  키워드북: 'lorebook',
  lorebook: 'lorebook',
}

function labelField(label: string): Field | undefined {
  const key = label.replace(/\s/g, '').toLowerCase()
  return Object.hasOwn(LABELS, key) ? LABELS[key] : undefined
}

function headingField(line: string): Field | undefined {
  const heading = /^#{1,2}\s+(.+?)\s*$/.exec(line)
  return heading ? labelField(heading[1]) : undefined
}

export async function parseImport({
  text,
}: {
  text: string
}): Promise<ImportResult> {
  if (text.length > IMPORT_MAX_CHARS)
    throw new Error('텍스트는 50,000자까지 가져올 수 있어요.')
  const groups: Record<Field, string[]> = {
    name: [],
    tagline: [],
    persona: [],
    habits: [],
    forbidden: [],
    worldName: [],
    world: [],
    context: [],
    greeting: [],
    lorebook: [],
  }
  const lines = text.split(/\r?\n/)
  const manuscript = lines.some((line) => headingField(line.trim()))
  let current: Field = 'persona'
  for (const raw of lines) {
    const line = raw.trim()
    const heading = headingField(line)
    if (heading) {
      current = heading
      continue
    }
    // 가이드 원고의 본문에 있는 “이름:”·“상황:”을 다른 필드로 옮기지 않는다.
    if (manuscript) {
      groups[current].push(raw)
      continue
    }
    if (!line) continue
    const match = /^(?:#{1,6}\s*)?([^:：]{1,20})\s*[:：]\s*(.*)$/.exec(line)
    if (match) {
      const field = labelField(match[1])
      if (field) {
        current = field
        if (match[2].trim()) groups[field].push(match[2].trim())
        continue
      }
    }
    groups[current].push(line)
  }
  const joined = (key: Field) => groups[key].join('\n').trim()
  const first = (key: Field) => groups[key].find((line) => line.trim())?.trim()
  const list = (key: Field) =>
    groups[key].map((line) => line.trim()).filter(Boolean)
  const context = joined('context') || undefined
  const greeting = joined('greeting') || undefined
  return {
    character: {
      name: first('name'),
      tagline: first('tagline'),
      persona: joined('persona') || undefined,
      habits: list('habits'),
      forbidden: list('forbidden'),
    },
    intro: context || greeting ? { context, greeting } : undefined,
    world:
      joined('world') || first('worldName')
        ? { name: first('worldName'), body: joined('world') }
        : undefined,
    lorebook: joined('lorebook') || undefined,
  }
}

export type ExpandField =
  | 'persona'
  | 'worldBody'
  | 'situation'
  | 'hiddenContext'
  | 'narrator'
  | 'commandDesc'
  | 'generic'
export type ExpandContext = {
  name?: string
  tagline?: string
  worldName?: string
}

export type ExpandInput = {
  field: ExpandField
  draft: string
  context?: ExpandContext
  workId?: string
  target: string
}

export async function expandField(
  input: ExpandInput,
  options: OperationOptions = {}
): Promise<{ text: string }> {
  const { expandStudioField } =
    await import('@/features/studio-renew/services/field-expansion.service')
  return expandStudioField(input, options)
}
